// src/components/EditPlayerForm.jsx
import React, { useState, useEffect } from 'react';
import PlayerSearch from './PlayerSearch';
import { ToastContainer, toast } from 'react-toastify';
import { updatePlayer, deletePlayer, enrichPlayerTM } from '../../services/api';
import { useFieldDefs } from '../../hooks/useFieldDefs';
import clubs from '../../data/clubs.json';
import clubsArg from '../../data/clubsArg.json';
import teammates from '../../data/teammates.json';
import countries from '../../data/country.json';
import coaches from '../../data/dts.json';
import positions from '../../data/positions.json';
import specificPositions from '../../data/specificPosition.json';
import '../AddPlayerForm/AddPlayerForm.css';

// El panel edita las dos poblaciones de jugadores, asi que el desplegable ofrece los clubes de
// los dos juegos. Se juntan SOLO aca: clubs.json alimenta ademas los tableros del juego global,
// el TeamSelector y el modo Fichaje, y meterle los argentinos los volveria casilla de un juego
// donde no van. El backend deduce el pool ('global' / 'arg') a partir de los clubes elegidos.
const clubesSeleccionables = { ...clubs, ...clubsArg };

const staticInitial = {
    id: '',
    name: '',
    country: '',
    position: '',
    birthDate: '',
    clubs: [],
    pools: [],
    poolsManual: false,
    teammates: [],
    countryFlag: '',
    image: '',
    africanPlayer: false,
    asianPlayer: false,
    champions: false,
    goldenBall: false,
    europaLeague: false,
    concacafPlayer: false,
    worldChampion: false,
    euroCup: false,
    copaAmerica: false,
    olympics: false,
    maxTransferValue: 0,
    goldenBoot: false,
    clubWorldChampion: false,
    goldenBoy: false,
    coaches: [],
    specificPositions: []
};

export default function EditPlayerForm() {
    // 1) Definiciones dinámicas
    const { fieldDefs: defs = [], loading, error } = useFieldDefs();

    // 2) Estado del jugador y original
    const [player, setPlayer] = useState(staticInitial);
    const [original, setOriginal] = useState(null);

    // TM URL field + enrich state
    const [tmUrl, setTmUrl] = useState('');
    const [tmLoading, setTmLoading] = useState(false);

    // 3) Cuando cambian defs, aseguro claves dinámicas en estado
    useEffect(() => {
        setPlayer(prev => {
            const base = { ...staticInitial, ...prev };
            defs.forEach(def => {
                if (!(def.key in base)) {
                    base[def.key] = def.type === 'checkbox' ? false : '';
                }
            });
            return base;
        });
    }, [defs]);

    // 4) callback de búsqueda
    const mapToState = sel => {
        // mapeo de campos conocidos
        const mapped = {
            id: sel._id,
            name: sel.nombre || '',
            country: Object.entries(countries).find(([c, n]) => n === sel.pais)?.[0] || '',
            position: sel.posicion || '',
            birthDate: sel.fecha_nacimiento.split('.').reverse().join('-'),
            clubs: sel.clubes || [],
            pools: sel.pools || [],
            poolsManual: Boolean(sel.pools_manual),
            teammates: sel.teammates || [],
            countryFlag: sel.img_pais || '',
            image: sel.img || '',
            africanPlayer: sel.african_player || false,
            asianPlayer: sel.asian_player || false,
            champions: sel.champions || false,
            goldenBall: sel.balon_oro || false,
            europaLeague: sel.europa_league || false,
            concacafPlayer: sel.concacaf_player || false,
            worldChampion: sel.campeon_mundo || false,
            euroCup: sel.eurocopa || false,
            copaAmerica: sel.copa_america || false,
            olympics: sel.jjoo || false,
            maxTransferValue: sel.maxTransferValue || 0,
            goldenBoot: sel.bota_oro || false,
            clubWorldChampion: sel.campeon_mundo_clubes || false,
            goldenBoy: sel.goldenboy || false,
            coaches: sel.dirigidoPor || [],
            specificPositions: sel.specificPosition || []
        };
        // agrego dinámicos desde sel[def.key]
        defs.forEach(def => {
            mapped[def.key] = sel[def.key] ?? (def.type === 'checkbox' ? false : '');
        });
        return mapped;
    };

    const handleSelect = sel => {
        const mp = mapToState(sel);
        setPlayer(mp);
        setOriginal(mp);
        setTmUrl(sel.tm_url || '');
    };

    // 5) manejadores genéricos
    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        setPlayer(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
            ...(name === 'country' ? { countryFlag: getCountryFlag(value) } : {})
        }));
    };
    const handleMulti = (e, field) => {
        const v = e.target.value;
        if (!v) return;
        setPlayer(prev => ({
            ...prev,
            [field]: Array.from(new Set([...prev[field], v]))
        }));
    };
    // Tildar un juego pasa el pool a manual: desde ahi el backend deja de calcularlo solo.
    const togglePool = pool => {
        setPlayer(prev => {
            const actual = prev.pools || [];
            const pools = actual.includes(pool) ? actual.filter(p => p !== pool) : [...actual, pool];
            return { ...prev, pools, poolsManual: true };
        });
    };

    const handleRemove = (item, field) => {
        setPlayer(prev => ({
            ...prev,
            [field]: prev[field].filter(i => i !== item)
        }));
    };

    const handleSubmit = async e => {
        e.preventDefault();
        // 1) filtrar vacíos
        const filt = Object.keys(player).reduce((acc, k) => {
            const v = player[k];
            if (v === '' || v === false) return acc;
            if (Array.isArray(v) && v.length === 0) return acc;
            acc[k] = v;
            return acc;
        }, {});
        // 2) ajustes country/fecha
        if (filt.country) {
            const c = filt.country.toLowerCase();
            filt.country = countries[c] || filt.country;
        }
        if (filt.birthDate) {
            const [y, m, d] = filt.birthDate.split('-');
            filt.birthDate = `${d}.${m}.${y}`;
        }
        // 3) payload en español + dinámicos
        const payload = {
            _id: filt.id,
            nombre: filt.name,
            nombre_lowercase: filt.name?.toLowerCase() || '',
            pais: filt.country,
            posicion: filt.position,
            fecha_nacimiento: filt.birthDate,
            clubes: filt.clubs,
            // Con poolsManual el admin eligio a mano en que juegos aparece y el backend lo
            // respeta; sin eso lo recalcula a partir de los clubes. Se lee de `player` porque
            // el paso 1 descarta los `false` y los arrays vacios.
            pools_manual: Boolean(player.poolsManual),
            ...(player.poolsManual ? { pools: player.pools } : {}),
            teammates: filt.teammates,
            img_pais: filt.countryFlag,
            img: filt.image,
            african_player: Boolean(filt.africanPlayer),
            asian_player: Boolean(filt.asianPlayer),
            champions: Boolean(filt.champions),
            balon_oro: Boolean(filt.goldenBall),
            europa_league: Boolean(filt.europaLeague),
            concacaf_player: Boolean(filt.concacafPlayer),
            campeon_mundo: Boolean(filt.worldChampion),
            eurocopa: Boolean(filt.euroCup),
            copa_america: Boolean(filt.copaAmerica),
            jjoo: Boolean(filt.olympics),
            maxTransferValue: filt.maxTransferValue,
            bota_oro: Boolean(filt.goldenBoot),
            campeon_mundo_clubes: Boolean(filt.clubWorldChampion),
            goldenboy: Boolean(filt.goldenBoy),
            dirigidoPor: filt.coaches,
            specificPosition: filt.specificPositions,
            // aquí los dinámicos. Los checkbox leen de `player` (no de `filt`): el paso 1
            // filtra los `false` de `filt` para no pisar otros campos vacíos, pero eso hacía
            // que desmarcar un checkbox (ej: premier_league) nunca mandara `false` al backend
            // — quedaba afuera del payload y el update (findByIdAndUpdate con $set implícito)
            // dejaba el valor viejo intacto en la DB.
            ...defs.reduce((o, def) => {
                if (def.type === 'checkbox') {
                    o[def.key] = Boolean(player[def.key]);
                } else if (def.key in filt) {
                    o[def.key] = filt[def.key];
                }
                return o;
            }, {})
        };
        if (player.poolsManual && (player.pools || []).length === 0) {
            toast.error('Elegí al menos un juego (global o argentino)');
            return;
        }
        try {
            await updatePlayer(payload);
            toast.success('Jugador actualizado con éxito');
            setPlayer(staticInitial);
            setOriginal(null);
        } catch (err) {
            console.error(err);
            toast.error('Error al actualizar');
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('¿Eliminar este jugador?')) return;
        try {
            await deletePlayer(player.id);
            toast.success('Jugador eliminado');
            setPlayer(staticInitial);
            setOriginal(null);
            setTmUrl('');
        } catch {
            toast.error('Error al eliminar');
        }
    };

    const handleEnrichTM = async () => {
        if (!tmUrl.trim() || !player.id) return;
        setTmLoading(true);
        try {
            const result = await enrichPlayerTM(player.id, tmUrl.trim());
            toast.success(`✓ Enriquecido con TM — ${result.fichajes} fichajes, ${result.logros_detallados} logros, ${result.palmares} palmarés`);
        } catch (e) {
            toast.error(`Error TM: ${e.message}`);
        } finally {
            setTmLoading(false);
        }
    };

    const getCountryFlag = code => `https://flagcdn.com/16x12/${code}.png`;

    // Componentes auxiliares (igual que en Add)
    const SelectField = ({ label, name, options, value, onChange, multiple, placeholder, selectedItems, onRemove }) => (
        <div className="form-group">
            <label htmlFor={name}>{label}</label>
            <select name={name} id={name} value={value} onChange={onChange} className="form-select">
                <option value="">{placeholder}</option>
                {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            {multiple && selectedItems && (
                <div className="selected-items">
                    {selectedItems.map((i, idx) =>
                        <div key={idx} className="selected-item">
                            {i} <button type="button" onClick={() => onRemove(i, name)}>x</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
    const CheckToggle = ({ label, name, value, onChange }) => (
        <div className="check-toggle">
            <label htmlFor={name} className="check-label">{label}</label>
            <div className="checkbox-container">
                <input type="checkbox" id={name} name={name} checked={value} onChange={onChange} />
                <label htmlFor={name} className="checkbox-label"></label>
            </div>
        </div>
    );

    if (loading) return <div>Cargando campos…</div>;
    if (error) return <div>Error: {error.message}</div>;

    return (
        <div className="form-container">
            <ToastContainer />
            <PlayerSearch onSelect={handleSelect} />
            {original && (
                <form onSubmit={handleSubmit} className="player-form">

                    {/* -- bloque estático igual que antes -- */}
                    <div className="form-columns">
                        <div className="left-column">
                            {/* Name & Country */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="name">Name</label>
                                    <input type="text" name="name" id="name"
                                        value={player.name} onChange={handleChange}
                                        required className="form-input" />
                                </div>
                                <SelectField
                                    label="Country" name="country"
                                    value={player.country} onChange={handleChange}
                                    placeholder="Select a country"
                                    options={Object.entries(countries).map(([c, n]) => ({ value: c, label: n }))}
                                />
                            </div>
                            {/* Specific + Position */}
                            <div className="form-row">
                                <SelectField
                                    label="Specific Position" name="specificPositions"
                                    value="" placeholder="Select a specific position"
                                    options={Object.keys(specificPositions).map(p => ({ value: p, label: p }))}
                                    multiple selectedItems={player.specificPositions}
                                    onChange={e => handleMulti(e, 'specificPositions')}
                                    onRemove={handleRemove}
                                />
                                <SelectField
                                    label="Position" name="position"
                                    value={player.position} onChange={handleChange}
                                    placeholder="Select a position"
                                    options={Object.keys(positions).map(p => ({ value: p, label: p }))}
                                />
                            </div>
                            {/* BirthDate + Coaches */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="birthDate">Birth Date</label>
                                    <input type="date" name="birthDate" id="birthDate"
                                        value={player.birthDate} onChange={handleChange}
                                        required className="form-input" />
                                </div>
                                <SelectField
                                    label="Coaches" name="coaches"
                                    value="" placeholder="Select coaches"
                                    options={Object.keys(coaches).map(c => ({ value: c, label: c }))}
                                    multiple selectedItems={player.coaches}
                                    onChange={e => handleMulti(e, 'coaches')}
                                    onRemove={handleRemove}
                                />
                            </div>
                            {/* Clubs + Teammates */}
                            <div className="form-row">
                                <SelectField
                                    label="Clubs" name="clubs"
                                    value="" placeholder="Select clubs"
                                    options={Object.keys(clubesSeleccionables).map(c => ({ value: c, label: c }))}
                                    multiple selectedItems={player.clubs}
                                    onChange={e => handleMulti(e, 'clubs')}
                                    onRemove={handleRemove}
                                />
                                {/* En que juegos aparece. Por defecto (automatico) el backend lo
                                    calcula a partir de los clubes al guardar. Tildar o destildar
                                    un juego lo pasa a manual y el backend lo respeta, incluso al
                                    refrescar desde TM; "Automatico" vuelve al calculo por clubes. */}
                                {player.id && (
                                    <div style={{ fontSize: 12, marginTop: 4 }}>
                                        <span style={{ opacity: 0.8 }}>Aparece en: </span>
                                        {[['global', '🌍 global'], ['arg', '🇦🇷 argentino']].map(([pool, label]) => (
                                            <label key={pool} style={{ marginRight: 10, cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={(player.pools || []).includes(pool)}
                                                    onChange={() => togglePool(pool)}
                                                    style={{ marginRight: 4 }}
                                                />
                                                {label}
                                            </label>
                                        ))}
                                        <label style={{ cursor: 'pointer', opacity: 0.8 }}>
                                            <input
                                                type="checkbox"
                                                checked={!player.poolsManual}
                                                onChange={e => setPlayer(prev => ({ ...prev, poolsManual: !e.target.checked }))}
                                                style={{ marginRight: 4 }}
                                            />
                                            Automático (según clubes)
                                        </label>
                                    </div>
                                )}
                                <SelectField
                                    label="Teammates" name="teammates"
                                    value="" placeholder="Select teammates"
                                    options={Object.keys(teammates).map(t => ({ value: t, label: t }))}
                                    multiple selectedItems={player.teammates}
                                    onChange={e => handleMulti(e, 'teammates')}
                                    onRemove={handleRemove}
                                />
                            </div>
                            {/* Value + Image */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="maxTransferValue">Transfer Value</label>
                                    <input type="number" name="maxTransferValue" id="maxTransferValue"
                                        value={player.maxTransferValue} onChange={handleChange}
                                        className="form-input" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="image">Image URL</label>
                                    <input type="text" name="image" id="image"
                                        value={player.image} onChange={handleChange}
                                        placeholder="Image URL" className="form-input" />
                                </div>
                            </div>
                        </div>

                        <div className="right-column">
                            <div className="check-grid">
                                {/* estáticos */}
                                {[
                                    ['africanPlayer', 'African Player'],
                                    ['asianPlayer', 'Asian Player'],
                                    ['champions', 'Champions'],
                                    ['goldenBall', 'Golden Ball'],
                                    ['europaLeague', 'Europa League'],
                                    ['concacafPlayer', 'Concacaf Player'],
                                    ['worldChampion', 'World Champion'],
                                    ['euroCup', 'Euro Cup'],
                                    ['copaAmerica', 'Copa America'],
                                    ['olympics', 'Olympics'],
                                    ['goldenBoot', 'Golden Boot'],
                                    ['clubWorldChampion', 'Club World Champion'],
                                    ['goldenBoy', 'Golden Boy']
                                ].map(([k, l]) => (
                                    <CheckToggle
                                        key={k} label={l} name={k}
                                        value={player[k]} onChange={handleChange}
                                    />
                                ))}
                                {/* dinámicos checkbox */}
                                {defs.filter(d => d.type === 'checkbox').map(def => (
                                    <CheckToggle
                                        key={def.key}
                                        label={def.label}
                                        name={def.key}
                                        value={player[def.key]}
                                        onChange={handleChange}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* campos dinámicos no-checkbox */}
                    {defs.some(d => d.type !== 'checkbox') && (
                        <fieldset className="dynamic-fields">
                            <legend>Campos Personalizados</legend>
                            {defs.filter(d => d.type !== 'checkbox').map(def => {
                                const val = player[def.key];
                                const onChange = e => {
                                    const { name, type, checked, value } = e.target;
                                    setPlayer(p => ({
                                        ...p,
                                        [name]: type === 'checkbox' ? checked : value
                                    }));
                                };
                                if (def.type === 'select') {
                                    return (
                                        <SelectField
                                            key={def.key}
                                            label={def.label}
                                            name={def.key}
                                            value={val}
                                            onChange={onChange}
                                            placeholder={`Seleccione ${def.label}`}
                                            options={def.options.map(o => ({ value: o, label: o }))}
                                        />
                                    );
                                }
                                return (
                                    <div className="form-group" key={def.key}>
                                        <label htmlFor={def.key}>{def.label}</label>
                                        <input
                                            id={def.key}
                                            name={def.key}
                                            type={def.type}
                                            value={val}
                                            onChange={onChange}
                                            className="form-input"
                                        />
                                    </div>
                                );
                            })}
                        </fieldset>
                    )}

                    {/* ── Transfermarkt URL ── */}
                    <div className="form-group" style={{ marginTop: '12px' }}>
                        <label htmlFor="tmUrlEdit">
                            Transfermarkt URL
                            <span style={{ fontWeight: 400, opacity: 0.6 }}> (enriquecer datos)</span>
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                                type="text"
                                id="tmUrlEdit"
                                className="form-input"
                                placeholder="https://www.transfermarkt.com/.../profil/spieler/XXXXX"
                                value={tmUrl}
                                onChange={e => setTmUrl(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleEnrichTM())}
                                style={{ flex: 1 }}
                            />
                            <button
                                type="button"
                                className="form-button"
                                onClick={handleEnrichTM}
                                disabled={tmLoading || !tmUrl.trim()}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {tmLoading ? 'Extrayendo…' : '⬇ Extraer TM'}
                            </button>
                        </div>
                    </div>

                    <button type="submit" className="form-button">Update Player</button>
                    <button type="button" onClick={() => { setPlayer(staticInitial); setOriginal(null); setTmUrl(''); }} className="form-button">Cancel</button>
                    <button type="button" onClick={handleDelete} className="form-button">Delete</button>
                </form>
            )}
        </div>
    );
}
